import { LeagueModel, SportModel } from "../models/Sport.tsx";

const named = (value?: string | null) => String(value ?? "").trim();

function compactLeagueName(value?: string | null) {
    return named(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export type PinnedCommercialLiveLeague = {
    name: string;
    aliases: string[];
    sportName: string;
    sportId: string;
    fallbackOrder: number;
};

/** Always shown in Commercial Live, even if today's live-event match fails. */
export const PINNED_COMMERCIAL_LIVE_LEAGUES: PinnedCommercialLiveLeague[] = [
    {
        name: "NHL",
        aliases: [
            "nhl",
            "national hockey league",
            "n.h.l.",
            "usa - nhl",
            "usa nhl",
        ],
        sportName: "Hockey",
        sportId: "hockey",
        fallbackOrder: 0,
    },
    {
        name: "NCAAF",
        aliases: [
            "ncaaf",
            "ncaa football",
            "college football",
            "n.c.a.a.f.",
            "usa - ncaaf",
            "usa ncaaf",
            "cfb",
        ],
        sportName: "Football",
        sportId: "football",
        fallbackOrder: 1,
    },
    {
        name: "MLB",
        aliases: [
            "mlb",
            "major league baseball",
            "m.l.b.",
            "usa - mlb",
            "usa mlb",
        ],
        sportName: "Baseball",
        sportId: "baseball",
        fallbackOrder: 2,
    },
    {
        name: "NFL",
        aliases: [
            "nfl",
            "national football league",
            "n.f.l.",
            "usa - nfl",
            "usa nfl",
        ],
        sportName: "Football",
        sportId: "football",
        fallbackOrder: 3,
    },
    {
        name: "NBA",
        aliases: [
            "nba",
            "national basketball association",
            "n.b.a.",
            "usa - nba",
            "usa nba",
        ],
        sportName: "Basketball",
        sportId: "basketball",
        fallbackOrder: 4,
    },
    {
        name: "WNBA",
        aliases: [
            "wnba",
            "women's national basketball association",
            "womens national basketball association",
            "w.n.b.a.",
            "usa - wnba",
            "usa wnba",
        ],
        sportName: "Basketball",
        sportId: "basketball",
        fallbackOrder: 5,
    },
];

const HIDDEN_COMMERCIAL_LIVE_MARKERS = ["brazil", "brasil"];

export function isHiddenCommercialLiveLeague(leagueName?: string | null): boolean {
    const name = named(leagueName).toLowerCase();
    if (!name) return false;
    return HIDDEN_COMMERCIAL_LIVE_MARKERS.some(marker => name.includes(marker));
}

function aliasSet(pinned: PinnedCommercialLiveLeague) {
    return new Set([pinned.name.toLowerCase(), ...pinned.aliases.map(a => a.toLowerCase())]);
}

function compactAliasSet(pinned: PinnedCommercialLiveLeague) {
    return new Set([...aliasSet(pinned)].map(compactLeagueName).filter(Boolean));
}

export function pinnedMatchesLeagueName(pinned: PinnedCommercialLiveLeague, leagueName?: string | null): boolean {
    const name = named(leagueName).toLowerCase();
    if (!name) return false;
    if (aliasSet(pinned).has(name)) return true;
    const compact = compactLeagueName(name);
    return Boolean(compact) && compactAliasSet(pinned).has(compact);
}

export function matchPinnedCommercialLiveLeague(leagueName?: string | null): PinnedCommercialLiveLeague | null {
    return PINNED_COMMERCIAL_LIVE_LEAGUES.find(pinned => pinnedMatchesLeagueName(pinned, leagueName)) ?? null;
}

export function resolvePinnedCatalogLeague(
    sportsList: SportModel[] | undefined,
    pinned: PinnedCommercialLiveLeague
): { sport: SportModel; league: LeagueModel } | null {
    const preferredSport =
        sportsList?.find(s => named(s.name).toLowerCase() === pinned.sportName.toLowerCase()) ??
        sportsList?.find(s => named(s.id).toLowerCase() === pinned.sportId.toLowerCase());

    const findLeague = (sport?: SportModel) =>
        sport?.leagues?.find(league => pinnedMatchesLeagueName(pinned, league.name));

    const leagueInPreferred = findLeague(preferredSport);
    if (preferredSport && leagueInPreferred) {
        return { sport: preferredSport, league: leagueInPreferred };
    }

    for (const sport of sportsList ?? []) {
        const league = findLeague(sport);
        if (league) return { sport, league };
    }

    if (preferredSport) {
        return {
            sport: preferredSport,
            league: {
                name: pinned.name,
                isActive: true,
            } as LeagueModel,
        };
    }

    return null;
}
